import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ActivePathsContext } from './SideBarItem';
import { isActiveInGroup } from './matchActivePath';

/**
 * A collapsible sidebar group. It re-opens by itself whenever the current
 * screen lives inside it, so the selected subitem is never hidden behind a
 * collapsed parent. The user can still collapse/expand it by hand while it is
 * not the active group.
 *
 * props:
 * - id: the submenu element id (ex: submenu-settings)
 * - text: Settings
 * - paths: destinations of the items below
 * - icon: the group icon
 * - children: the nested SideBarItem list
 */
function SideBarGroup(props) {
    const { pathname } = useLocation();
    const allPaths = useContext(ActivePathsContext) || props.paths;
    const isActive = isActiveInGroup(pathname, props.paths, allPaths);
    const [open, setOpen] = useState(isActive);

    // Navigating back into the group re-expands it, even after a manual collapse.
    useEffect(() => {
        if (isActive) setOpen(true);
    }, [isActive, pathname]);

    return (
        <li className="nav-item">
            <span
                className="nav-link d-flex justify-content-between align-items-center"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={props.id}
                onClick={() => setOpen(!open)}
                onKeyDown={event => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setOpen(!open);
                }}
            >
                <span>
                    <span className="sidebar-icon">{props.icon}</span>
                    <span className="sidebar-text">{props.text}</span>
                </span>
                <span
                    className="link-arrow"
                    style={{
                        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <svg
                        className="icon icon-sm"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                </span>
            </span>
            <div
                className={'multi-level collapse' + (open ? ' show' : '')}
                role="list"
                id={props.id}
            >
                <ul className="flex-column nav">{props.children}</ul>
            </div>
        </li>
    );
}

export default SideBarGroup;
